import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import logo from '../img/ast_ylw_logo.png';
import profileIcon from '../img/profile.png';
import './Header1.css';

function AdminHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isSetupDropdownOpen, setIsSetupDropdownOpen] = useState(false);
  const [isMobileSetupOpen, setIsMobileSetupOpen] = useState(false);
  const setupDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (setupDropdownRef.current && !setupDropdownRef.current.contains(event.target)) {
        setIsSetupDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsProfileMenuOpen(false);
    navigate('/login');
  };

  const handleProfileClick = () => {
    navigate('/Adminprofile');
    setIsMenuOpen(false);
  };

  const handleNavClick = (path) => {
    navigate(path);
    setIsMenuOpen(false);
    setIsSetupDropdownOpen(false);
    setIsMobileSetupOpen(false);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Check if any Setup route is active
  const isSetupActive = () => {
    return ['/tournament-setup', '/EventCategoriesSetup', '/Eventtypessetup','/rounds-setup','match-scheduler'].some(path =>
      location.pathname === path
    );
  };

  // Main navigation items (non-dropdown)
  const mainNavItems = [
    { label: 'Tournament Dashboard', path: '/tournamentdashboard' },
    { label: 'Score Entry', path: '/score-entry' },
    { label: 'Tournament List', path: '/tournament-list' },
    { label: 'Users list', path: '/Userlist' },
    { label: 'Game List', path: '/Gamelist' },
    { label: 'Sponsors', path: '/sponsors' }
  ];

  // Setup dropdown items
  const setupItems = [
      { label: 'Event Types', path: '/Eventtypessetup'},
      { label: 'Event Categories', path: '/EventCategoriesSetup' },
      { label: 'Tournament Setup', path: '/tournament-setup' },
       { label: 'Rounds Setup', path: '/rounds-setup' },  // ← NEW
      {label:'Tournament Match Scheduler',path:'/match-scheduler'}
  ];

  return (
    <header className="simple-header sticky-top bg-white">
      <div className="container-fluid d-flex justify-content-between align-items-center py-4 px-3">
        
        {/* Logo */}
        <div className="logo-container" onClick={() => handleNavClick('/AdminDashboard')}>
          <img src={logo} alt="Logo" className="header-logo" />
        </div>

        {/* Hamburger Menu Button - Mobile */}
        <button
          className="hamburger-btn d-lg-none"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Desktop Navigation */}
        <nav className="nav-links d-none d-lg-flex align-items-center">
          {/* Dashboard */}
          <button
            className={`nav-link-btn ${isActive('/AdminDashboard') ? 'active-link' : ''}`}
            onClick={() => handleNavClick('/AdminDashboard')}
          >
            Dashboard
          </button>

          {/* Setup Dropdown */}
          <div className="nav-dropdown" ref={setupDropdownRef}>
            <button
              className={`nav-link-btn nav-dropdown-toggle ${isSetupActive() ? 'active-link' : ''}`}
              onClick={() => setIsSetupDropdownOpen(!isSetupDropdownOpen)}
            >
              Setup
              <ChevronDown 
                size={16} 
                className={`dropdown-arrow ${isSetupDropdownOpen ? 'rotate' : ''}`}
              />
            </button>

            {isSetupDropdownOpen && (
              <div className="setup-dropdown-menu">
                {setupItems.map((item) => (
                  <button
                    key={item.path}
                    className={`setup-dropdown-item ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => handleNavClick(item.path)}
                  >
                    <span className="dropdown-icon">{item.icon}</span>
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Other Navigation Items */}
          {mainNavItems.map((item) => (
            <button
              key={item.path}
              className={`nav-link-btn ${isActive(item.path) ? 'active-link' : ''}`}
              onClick={() => handleNavClick(item.path)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* Profile Icon - Desktop */}
        <div className="profile-container d-none d-lg-block">
          <div
            className="profile-wrapper"
            onMouseEnter={() => setIsProfileMenuOpen(true)}
            onMouseLeave={() => setIsProfileMenuOpen(false)}
          >
            <img
              src={profileIcon}
              alt="Profile"
              className="profile-img"
              onClick={handleProfileClick}
            />

            {isProfileMenuOpen && (
              <div className="profile-dropdown">
                <button className="logout-btn" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
        <nav className="mobile-nav">
          {/* Dashboard */}
          <button
            className={`nav-link-btn ${isActive('/AdminDashboard') ? 'active-link' : ''}`}
            onClick={() => handleNavClick('/AdminDashboard')}
          >
            Dashboard
          </button>

          {/* Setup Mobile Dropdown */}
          <div className="mobile-dropdown-section">
            <button
              className={`nav-link-btn mobile-dropdown-toggle ${isSetupActive() ? 'active-link' : ''}`}
              onClick={() => setIsMobileSetupOpen(!isMobileSetupOpen)}
            >
              Setup
              <ChevronDown 
                size={16} 
                className={`dropdown-arrow ${isMobileSetupOpen ? 'rotate' : ''}`}
              />
            </button>

            {isMobileSetupOpen && (
              <div className="mobile-dropdown-items">
                {setupItems.map((item) => (
                  <button
                    key={item.path}
                    className={`nav-link-btn mobile-sub-item ${isActive(item.path) ? 'active-link' : ''}`}
                    onClick={() => handleNavClick(item.path)}
                  >
                    <span className="dropdown-icon">{item.icon}</span>
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Other Navigation Items */}
          {mainNavItems.map((item) => (
            <button
              key={item.path}
              className={`nav-link-btn ${isActive(item.path) ? 'active-link' : ''}`}
              onClick={() => handleNavClick(item.path)}
            >
              {item.label}
            </button>
          ))}

          {/* Mobile Profile */}
          <div className="mobile-profile" onClick={handleProfileClick}>
            <img src={profileIcon} alt="Profile" className="profile-img-mobile" />
            <span className="profile-text">Profile</span>
          </div>

          {/* Logout (Mobile) */}
          <button className="mobile-logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </nav>
      </div>
    </header>
  );
}

export default AdminHeader;